import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Box, useToaster } from "@dealer-center/core-components";
import {
  NRT_USER_PERMISSION_GROUPS_QUERY_KEY,
  isNrtRequestError,
  useDeleteNrtRecord,
  useNrtRecords
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-fetch";
import type { NrtRecord } from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import { DeleteConfirmationModal } from "./Display/DeleteConfirmationModal";
import { Header } from "./Display/Header";
import { RecordModal } from "./Display/RecordModal";
import { RecordsTable } from "./Display/RecordsTable";
import { SR_ONLY_SX } from "./Display/srOnlySx";
import { useNrtPermissions } from "./useNrtPermissions";

const PAGE_TITLE = "NRT Mapping Tool";

// Exported so specs assert the real copy instead of a duplicate that drifts.
export const PERMISSIONS_UNAVAILABLE_MESSAGE =
  "We could not determine your NRT permissions, so these records are read-only. " +
  "Try reloading the page.";

// Exported so specs assert the real copy instead of a duplicate that drifts.
export const DELETE_SUCCEEDED_MESSAGE = "Record deleted successfully!";

// Exported so specs assert the real copy instead of a duplicate that drifts.
export const DELETE_FAILED_MESSAGE =
  "We could not delete this record. It may have already been removed, or " +
  "something went wrong. Please try again.";

// Module-level so an empty list keeps a stable identity while records load —
// RecordsTable memoizes its columns against this array.
const NO_RECORDS: NrtRecord[] = [];

// `record` is only set in edit/view mode; closing drops it so the next open
// starts from a clean form.
type ModalState =
  | { isOpen: false }
  | { isOpen: true; mode: "add" }
  | { isOpen: true; mode: "edit"; record: NrtRecord }
  | { isOpen: true; mode: "view"; record: NrtRecord };

export const PagesNrtDatabaseFilterToolUi = () => {
  const { data, error, isLoading } = useNrtRecords();
  const records = data ?? NO_RECORDS;
  const { permissions, currentUserId, isIndeterminate } = useNrtPermissions();
  const [modalState, setModalState] = useState<ModalState>({ isOpen: false });
  const [recordPendingDelete, setRecordPendingDelete] =
    useState<NrtRecord | null>(null);
  const deleteRecordMutation = useDeleteNrtRecord();
  const { toastError, toastSuccess } = useToaster();
  const queryClient = useQueryClient();

  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  const openAddModal = useCallback(
    () => setModalState({ isOpen: true, mode: "add" }),
    []
  );

  const openEditModal = useCallback(
    (record: NrtRecord) =>
      setModalState({ isOpen: true, mode: "edit", record }),
    []
  );

  const openLookModal = useCallback(
    (record: NrtRecord) =>
      setModalState({ isOpen: true, mode: "view", record }),
    []
  );

  const closeModal = useCallback(() => setModalState({ isOpen: false }), []);

  // Cancel must send no request (AC US3.3) — clearing the pending record is
  // the only effect.
  const closeDeleteConfirmation = useCallback(
    () => setRecordPendingDelete(null),
    []
  );

  // Reset any previous delete's error/success state so re-opening the
  // dialog for a different record starts clean.
  const openDeleteConfirmation = useCallback(
    (record: NrtRecord) => {
      deleteRecordMutation.reset();
      setRecordPendingDelete(record);
    },
    [deleteRecordMutation]
  );

  // Closes on success and failure; toasts are visual-only, so
  // deleteAnnouncement below covers screen readers.
  const confirmDelete = useCallback(() => {
    if (!recordPendingDelete) {
      return;
    }
    deleteRecordMutation.mutate(
      {
        accountId: recordPendingDelete.accountId,
        id: recordPendingDelete.id
      },
      {
        onSuccess: () => toastSuccess("Success", DELETE_SUCCEEDED_MESSAGE),
        onError: err => {
          toastError(DELETE_FAILED_MESSAGE);
          // A 403 means the role set the button used is stale — re-read it.
          if (isNrtRequestError(err) && err.status === 403) {
            queryClient.invalidateQueries({
              queryKey: NRT_USER_PERMISSION_GROUPS_QUERY_KEY
            });
          }
        },
        onSettled: () => setRecordPendingDelete(null)
      }
    );
  }, [
    deleteRecordMutation,
    queryClient,
    recordPendingDelete,
    toastError,
    toastSuccess
  ]);

  // The table's spinner and record count are silent to a screen reader.
  const statusAnnouncement = isLoading
    ? "Loading mapping records"
    : `${records.length} mapping records loaded`;

  // Separate from statusAnnouncement so it never re-announces the count.
  const deleteAnnouncement = deleteRecordMutation.isSuccess
    ? DELETE_SUCCEEDED_MESSAGE
    : deleteRecordMutation.isError
      ? DELETE_FAILED_MESSAGE
      : "";

  return (
    // minWidth: 0 lets flex children shrink below their content width.
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        minWidth: 0
      }}
    >
      <Header
        canCreate={permissions.canCreate}
        subtitle="View, edit, and manage data mappings"
        title={PAGE_TITLE}
        onAddNewRecord={openAddModal}
      />
      <Box
        aria-atomic="true"
        aria-live="polite"
        data-testid="nrt-records-live-region"
        role="status"
        sx={SR_ONLY_SX}
      >
        {statusAnnouncement}
      </Box>
      <Box
        aria-atomic="true"
        aria-live="polite"
        data-testid="nrt-delete-live-region"
        role="status"
        sx={SR_ONLY_SX}
      >
        {deleteAnnouncement}
      </Box>
      {/* Read-only is already enforced by `permissions`; this only explains
          why, so the state is not mistaken for "you have no access". */}
      {isIndeterminate && (
        <Alert
          data-testid="nrt-permissions-unavailable"
          role="status"
          type="caution"
        >
          {PERMISSIONS_UNAVAILABLE_MESSAGE}
        </Alert>
      )}
      {error && (
        <Alert role="alert" type="error">
          {error.message || "Failed to load NRT records. Please try again."}
        </Alert>
      )}
      {/* Kept alongside the error: a failed refetch shouldn't blank the table. */}
      {!(error && records.length === 0) && (
        // isLoading only; isFetching is also true on background refetches.
        <RecordsTable
          isLoading={isLoading}
          permissions={permissions}
          records={records}
          onDeleteRecord={openDeleteConfirmation}
          onEditRecord={openEditModal}
          onLookRecord={openLookModal}
        />
      )}
      {/* Unmounted when closed, so the form state resets between opens. */}
      {modalState.isOpen && (
        <RecordModal
          currentUserId={currentUserId}
          initialValues={
            modalState.mode === "add" ? undefined : modalState.record
          }
          mode={modalState.mode}
          onClose={closeModal}
        />
      )}
      {/* Unmounted when closed, mirroring RecordModal's reset-on-reopen behavior. */}
      {recordPendingDelete && (
        <DeleteConfirmationModal
          isProcessing={deleteRecordMutation.isPending}
          onCancel={closeDeleteConfirmation}
          onConfirm={confirmDelete}
        />
      )}
    </Box>
  );
};
